import {
  Banner,
  WarningIcon,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'
import { addDays } from 'date-fns'

import { useUser } from '../../../../common/context/User'
import useDismissObjects from '../../../../common/hooks/useDismissObjects'
import useGetPortalSession from '../../../../common/hooks/useGetPortalSession'
import {
  getBillingPageUrl,
  getBillingPaymentMethodUrl,
  redirectToUrl,
} from '../../../../common/utils/urls'
import { isCanceledAtPeriodEnd } from '../../../../common/utils/user'
import UpgradePathsTracking from '../../../../tracking/UpgradePaths'
import {
  getBillingBannerDismissalId,
  getBillingConditionDismissalExpiryDays,
  getVisibleBillingConditions,
} from '../../utils/billingBannerConditions'
import type { BillingCondition } from '../../utils/billingBannerConditions'

import type { JSX } from 'react'

const pastDueTrackingPayload = {
  ctaButton: 'updatePaymentDetails',
  ctaView: 'banner',
  ctaLocation: 'navBar',
  ctaVersion: '1',
  upgradePathName: 'pastDue-upgrade',
}

const multipleIssuesTrackingPayload = {
  ctaButton: 'goToBilling',
  ctaView: 'banner',
  ctaLocation: 'navBar',
  ctaVersion: '1',
  upgradePathName: 'billingIssues-upgrade',
}

const MOBILE_COPY: Record<BillingCondition, string> = {
  'payment-past-due': 'Issue charging your last payment',
  'expired-card': 'Your card has expired',
  'expiring-card': 'Your card is expiring soon',
  'missing-billing-address': 'Please add a billing address',
}

const DESKTOP_COPY: Record<
  Exclude<BillingCondition, 'missing-billing-address'>,
  string
> = {
  'payment-past-due':
    'We had a problem charging your last payment. Please update your payment details to prevent losing access to your features.',
  'expired-card':
    'Your card has expired. To keep your subscription running smoothly, update your billing details.',
  'expiring-card': `Your card's about to expire. To keep your subscription running smoothly, update your billing details.`,
}

const BillingBanner = (): JSX.Element | null => {
  const user = useUser()
  const isMobile = useIsMobileScreenSize()
  const { trackCTAClicked } = UpgradePathsTracking(user)
  const { openBillingPortal } = useGetPortalSession()
  const { onDismissObjects } = useDismissObjects({
    organizationId: user.currentOrganization?.id,
  })

  const conditions = getVisibleBillingConditions(user)
  if (conditions.length === 0) return null

  const handleDismiss = (): void => {
    onDismissObjects(
      conditions.map((condition) => {
        const expiryDays = getBillingConditionDismissalExpiryDays(condition)
        return {
          id: getBillingBannerDismissalId(user, condition),
          ...(expiryDays !== null
            ? { expiresAt: addDays(new Date(), expiryDays).toISOString() }
            : {}),
        }
      }),
    )
  }

  if (conditions.length > 1) {
    return (
      <Banner variant="critical" data-testid="billing-banner">
        <Banner.Text>Billing information needs your attention</Banner.Text>
        <Banner.Action
          onClick={(): void => {
            trackCTAClicked(multipleIssuesTrackingPayload)
            redirectToUrl(getBillingPageUrl(null))
          }}
        >
          Go to Billing
        </Banner.Action>
        <Banner.Dismiss onClick={handleDismiss}>Dismiss</Banner.Dismiss>
      </Banner>
    )
  }

  const condition = conditions[0]
  const variant = condition === 'expiring-card' ? 'warning' : 'critical'

  const desktopCopy =
    condition === 'missing-billing-address'
      ? isCanceledAtPeriodEnd(user)
        ? 'You do not have a billing address on file. Please add your info.'
        : 'You do not have a billing address on file. Please add your info to ensure your subscription remains in good standing.'
      : DESKTOP_COPY[condition]

  const ctaLabel =
    condition === 'missing-billing-address'
      ? 'Add Billing Address'
      : 'Update Payment Details'

  const handleCtaClick = (): void => {
    switch (condition) {
      case 'payment-past-due':
        trackCTAClicked(pastDueTrackingPayload)
        redirectToUrl(getBillingPageUrl('update-payment-method'))
        break
      case 'expired-card':
      case 'expiring-card':
        redirectToUrl(getBillingPaymentMethodUrl())
        break
      case 'missing-billing-address':
        openBillingPortal({
          ctaClicked: true,
          cta: 'banner-missingBillingAddress-addBillingAddress-1',
        })
        break
    }
  }

  return (
    <Banner variant={variant} data-testid="billing-banner">
      <Banner.Text icon={isMobile ? <WarningIcon /> : undefined}>
        {isMobile ? (
          MOBILE_COPY[condition]
        ) : (
          <>
            <strong>ACTION REQUIRED:</strong> {desktopCopy}
          </>
        )}
      </Banner.Text>
      <Banner.Action onClick={handleCtaClick}>{ctaLabel}</Banner.Action>
      <Banner.Dismiss onClick={handleDismiss}>Dismiss</Banner.Dismiss>
    </Banner>
  )
}

export default BillingBanner
